import React, { useState } from 'react';
import { X, Music, Image, Mic, Settings, Bell, Volume2, Upload, Check, User, Lock, SlidersHorizontal } from 'lucide-react';
import { Slider } from './ui/slider';
import BackgroundTab from './settings/BackgroundTab';

// Programmes de respiration
const BREATHING_PROGRAMS = [
  {
    id: 'coherence',
    name: 'Cohérence cardiaque',
    description: 'Équilibre parfait inspiration/expiration',
    phases: [
      { name: 'Inspirez', duration: 5, action: 'inhale' },
      { name: 'Expirez', duration: 5, action: 'exhale' }
    ],
    color: 'from-amber-500 to-cyan-500'
  },
  {
    id: '478',
    name: '4-7-8 Relaxant',
    description: 'Technique de relaxation profonde',
    phases: [
      { name: 'Inspirez', duration: 4, action: 'inhale' },
      { name: 'Retenez', duration: 7, action: 'hold' },
      { name: 'Expirez', duration: 8, action: 'exhale' }
    ],
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'box',
    name: 'Respiration Équilibrée',
    description: 'Respiration carrée équilibrée',
    phases: [
      { name: 'Inspirez', duration: 5, action: 'inhale' },
      { name: 'Retenez', duration: 5, action: 'hold' },
      { name: 'Expirez', duration: 5, action: 'exhale' },
      { name: 'Pause', duration: 5, action: 'pause' }
    ],
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'energizing',
    name: 'Énergisant',
    description: 'Boost d\'énergie rapide',
    phases: [
      { name: 'Inspirez', duration: 4, action: 'inhale' },
      { name: 'Retenez', duration: 2, action: 'hold' },
      { name: 'Expirez', duration: 4, action: 'exhale' }
    ],
    color: 'from-orange-500 to-red-500'
  }
];

const BreathingSettingsPanel = ({
  isOpen,
  onClose,
  // Programme
  selectedProgram,
  onProgramChange,
  totalDuration,
  onDurationChange,
  // Visuel
  bgOpacity,
  onBgOpacityChange,
  menuTransparency,
  onMenuTransparencyChange,
  // Audio
  breathingMusic,
  selectedMusic,
  onMusicSelect,
  musicVolume,
  onMusicVolumeChange,
  ambientSounds,
  activeAmbients,
  onAmbientToggle,
  ambientVolumes,
  onAmbientVolumeChange,
  phaseSoundsEnabled,
  onPhaseSoundsToggle,
  phaseSoundVolume,
  onPhaseSoundVolumeChange,
  // Voix
  voiceGuidanceEnabled,
  onVoiceGuidanceToggle,
  selectedVoiceType,
  onVoiceTypeChange,
  coachGender,
  onCoachGenderChange,
  maleVoices,
  femaleCoachVoices,
  femaleTahitianVoices,
  customVoices,
  voiceVolume,
  onVoiceVolumeChange,
  onVoiceUpload,
  uploadingVoice,
  // Admin
  isAdmin,
  lockedVoices,
  onToggleLockVoice,
  onDeleteVoice,
  // Background
  backgrounds,
  customVideos,
  selectedBg,
  onBgSelect,
  backgroundImages,
  selectedImageBg,
  onImageBgSelect,
  hoveredVideo,
  onHoverVideo,
  onVideoUpload,
  onVideoRename,
  onVideoToggleLock,
  onVideoDelete,
  defaultVideoId,
  onSetDefaultVideo,
  uploadingVideo,
  onImageUpload,
  uploadingImage,
  selectedAnimation,
  onAnimationChange,
  defaultImageId,
  onSetDefaultImage,
  onImageRename,
  onImageToggleLock,
  onImageDelete
}) => {
  const [activeTab, setActiveTab] = useState('program');

  if (!isOpen) return null;

  const TABS = [
    { id: 'program', label: 'Programme', icon: Settings },
    { id: 'audio', label: 'Audio', icon: Music },
    { id: 'voice', label: 'Voix', icon: Mic },
    { id: 'background', label: 'Fond', icon: Image }
  ];

  return (
    <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
    <div className="w-[420px] max-h-[80vh] bg-slate-900/98 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl pointer-events-auto panel-slide-in">
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-gradient-to-r from-amber-500/10 to-purple-500/10">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-heading font-semibold text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-400" />
            Paramétrer votre séance
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-2 text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                activeTab === tab.id
                  ? 'text-amber-400 border-b-2 border-amber-400 bg-amber-500/10'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="max-h-[60vh] overflow-y-auto">
        {/* Programme Tab */}
        {activeTab === 'program' && (
          <div className="p-4 space-y-4">
            {/* Durée */}
            <div className="p-3 bg-white/5 rounded-xl">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Durée de la séance</p>
              <div className="flex gap-2">
                {[3, 5, 10, 15].map((min) => (
                  <button
                    key={min}
                    onClick={() => onDurationChange(min)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      totalDuration === min
                        ? 'bg-amber-500 text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {min} min
                  </button>
                ))}
              </div>
            </div>

            {/* Programmes */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Programme de respiration</p>
              <div className="space-y-2">
                {BREATHING_PROGRAMS.map((program) => (
                  <button
                    key={program.id}
                    onClick={() => onProgramChange(program)}
                    className={`w-full p-3 rounded-xl text-left transition-all ${
                      selectedProgram.id === program.id
                        ? `bg-gradient-to-r ${program.color} text-white`
                        : 'bg-white/5 hover:bg-white/10 text-white'
                    }`}
                  >
                    <p className="font-semibold text-sm">{program.name}</p>
                    <p className={`text-xs ${selectedProgram.id === program.id ? 'text-white/80' : 'text-white/50'}`}>
                      {program.description}
                    </p>
                    <div className="flex gap-1 mt-2">
                      {program.phases.map((phase, i) => (
                        <span key={`${program.id}-${phase.action}-${i}`} className={`text-xs px-2 py-0.5 rounded ${
                          selectedProgram.id === program.id ? 'bg-white/20' : 'bg-white/5'
                        }`}>
                          {phase.duration}s
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Réglages visuels */}
            <div className="p-3 bg-white/5 rounded-xl space-y-4">
              <p className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Réglages visuels
              </p>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/60 text-sm">Opacité du fond</span>
                  <span className="text-white/80 text-sm">{bgOpacity}%</span>
                </div>
                <Slider
                  value={[bgOpacity]}
                  onValueChange={([v]) => onBgOpacityChange(v)}
                  max={80}
                  step={5}
                  className="w-full"
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/60 text-sm">Transparence menus</span>
                  <span className="text-white/80 text-sm">{menuTransparency}%</span>
                </div>
                <Slider
                  value={[menuTransparency]}
                  onValueChange={([v]) => onMenuTransparencyChange(v)}
                  max={80}
                  step={5}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        {/* Audio Tab */}
        {activeTab === 'audio' && (
          <div className="p-4 space-y-4">
            {/* Sons de guidance */}
            <div className="p-3 bg-white/5 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Sons de guidance
                </p>
                <button
                  onClick={onPhaseSoundsToggle}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${
                    phaseSoundsEnabled
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-white/5 text-white/40'
                  }`}
                >
                  {phaseSoundsEnabled ? 'Activé' : 'Désactivé'}
                </button>
              </div>
              {phaseSoundsEnabled && (
                <div className="flex items-center gap-3">
                  <Bell className="w-4 h-4 text-amber-400" />
                  <Slider
                    value={[phaseSoundVolume * 100]}
                    onValueChange={([v]) => onPhaseSoundVolumeChange(v / 100)}
                    max={100}
                    className="flex-1"
                  />
                  <span className="text-white/60 text-xs w-8">{Math.round(phaseSoundVolume * 100)}%</span>
                </div>
              )}
            </div>

            {/* Musiques */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Musique de fond</p>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {breathingMusic.map((music) => (
                  <button
                    key={music.id}
                    onClick={() => onMusicSelect(selectedMusic?.id === music.id ? null : music)}
                    className={`p-2 rounded-lg text-left transition-all text-xs ${
                      selectedMusic?.id === music.id
                        ? 'bg-amber-500/30 border border-amber-400/50'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Music className="w-3 h-3 text-amber-400 mb-1" />
                    <p className="text-white truncate">{music.name}</p>
                  </button>
                ))}
              </div>
              {selectedMusic && (
                <div className="mt-3 flex items-center gap-3">
                  <Volume2 className="w-4 h-4 text-amber-400" />
                  <Slider
                    value={[musicVolume * 100]}
                    onValueChange={([v]) => onMusicVolumeChange(v / 100)}
                    max={100}
                    className="flex-1"
                  />
                  <span className="text-white/60 text-xs w-8">{Math.round(musicVolume * 100)}%</span>
                </div>
              )}
            </div>

            {/* Sons ambiants */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Sons ambiants</p>
              <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
                {ambientSounds.map((sound) => {
                  const isActive = !!activeAmbients[sound.id];
                  return (
                    <button
                      key={sound.id}
                      onClick={() => onAmbientToggle(sound)}
                      className={`p-2 rounded-lg transition-all flex flex-col items-center gap-1 ${
                        isActive ? 'bg-amber-500/30 border border-amber-400/50' : 'bg-white/5 hover:bg-white/10'
                      }`}
                      data-testid={`ambient-sound-${sound.id}`}
                    >
                      <span className="text-xs text-white/70 truncate w-full text-center leading-tight">{sound.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Voice Tab */}
        {activeTab === 'voice' && (
          <div className="p-4 space-y-4">
            {/* Toggle guidance vocale */}
            <div className="p-3 bg-white/5 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/60 text-xs uppercase tracking-wider">Guidance vocale</p>
                <button
                  onClick={onVoiceGuidanceToggle}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${
                    voiceGuidanceEnabled
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-white/5 text-white/40'
                  }`}
                >
                  {voiceGuidanceEnabled ? 'Activée' : 'Désactivée'}
                </button>
              </div>
              {voiceGuidanceEnabled && (
                <div className="flex items-center gap-3">
                  <Volume2 className="w-4 h-4 text-amber-400" />
                  <Slider
                    value={[voiceVolume * 100]}
                    onValueChange={([v]) => onVoiceVolumeChange(v / 100)}
                    max={100}
                    className="flex-1"
                  />
                  <span className="text-white/60 text-xs w-8">{Math.round(voiceVolume * 100)}%</span>
                </div>
              )}
            </div>

            {/* Choix du type de voix - 4 options */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onVoiceTypeChange('male')}
                className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                  selectedVoiceType === 'male'
                    ? 'bg-blue-500/30 border border-blue-400/50 text-blue-400'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                }`}
              >
                <User className="w-4 h-4" />
                Homme
              </button>
              <button
                onClick={() => onVoiceTypeChange('female_coach')}
                className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                  selectedVoiceType === 'female_coach'
                    ? 'bg-amber-500/30 border border-amber-400/50 text-amber-400'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                }`}
              >
                <Mic className="w-4 h-4" />
                Coach
              </button>
              <button
                onClick={() => onVoiceTypeChange('female_tahitian')}
                className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                  selectedVoiceType === 'female_tahitian'
                    ? 'bg-emerald-500/30 border border-emerald-400/50 text-emerald-400'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                }`}
              >
                <Mic className="w-4 h-4" />
                Tahitienne
              </button>
              <button
                onClick={() => onVoiceTypeChange('custom')}
                className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                  selectedVoiceType === 'custom'
                    ? 'bg-pink-500/30 border border-pink-400/50 text-pink-400'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                }`}
              >
                <Upload className="w-4 h-4" />
                Ma Voix
              </button>
            </div>

            {/* Affichage des voix selon le type */}
            {selectedVoiceType === 'female_coach' ? (
              <div className="p-3 bg-white/5 rounded-xl" data-testid="coach-edge-voice-selector">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Voix naturelle française</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="coach-voice-female-btn"
                    onClick={() => onCoachGenderChange && onCoachGenderChange('female')}
                    className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                      coachGender === 'female' ? 'bg-amber-500/30 border border-amber-400/50 text-amber-300' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}>
                    Denise
                    <span className="text-[10px] opacity-70">Féminine</span>
                  </button>
                  <button
                    data-testid="coach-voice-male-btn"
                    onClick={() => onCoachGenderChange && onCoachGenderChange('male')}
                    className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                      coachGender === 'male' ? 'bg-amber-500/30 border border-amber-400/50 text-amber-300' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}>
                    Henri
                    <span className="text-[10px] opacity-70">Masculine</span>
                  </button>
                </div>
                <p className="text-white/40 text-[10px] mt-2">Voix neurales Microsoft — naturelles et gratuites</p>
              </div>
            ) : (
            <div className="p-3 bg-white/5 rounded-xl">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">
                {selectedVoiceType === 'male' ? 'Voix Homme' : selectedVoiceType === 'female_coach' ? 'Voix Coach Femme' : selectedVoiceType === 'female_tahitian' ? 'Voix Tahitienne' : 'Mes enregistrements'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {['inhale', 'exhale', 'hold', 'pause'].map((phase) => {
                  const voices = selectedVoiceType === 'male' ? maleVoices : selectedVoiceType === 'female_coach' ? femaleCoachVoices : selectedVoiceType === 'female_tahitian' ? (femaleTahitianVoices || {}) : customVoices;
                  const hasVoice = voices[phase];
                  const labels = { inhale: 'Inspirez', exhale: 'Expirez', hold: 'Retenez', pause: 'Pause' };
                  const isLocked = lockedVoices?.[`${selectedVoiceType}_${phase}`];
                  return (
                    <div
                      key={phase}
                      className={`p-2 rounded-lg ${hasVoice ? 'bg-white/10' : 'bg-white/5'}`}
                    >
                      <p className="text-white/70 text-xs mb-1">{labels[phase]}</p>
                      {hasVoice ? (
                        <div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              {isLocked ? <Lock className="w-3 h-3 text-amber-400" /> : <Check className="w-3 h-3 text-green-400" />}
                              <span className={`text-xs ${isLocked ? 'text-amber-400' : 'text-green-400'}`}>{isLocked ? 'Verrouillé' : 'Prêt'}</span>
                            </div>
                            {isAdmin && !isLocked && (
                              <button className="cursor-pointer text-amber-400/60 text-[10px] hover:text-amber-400 transition-colors"
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.accept = '.mp3,.wav,.ogg,.m4a';
                                  input.onchange = (e) => { const file = e.target.files[0]; if (file) onVoiceUpload(phase, file, selectedVoiceType); };
                                  input.click();
                                }}>
                                Remplacer
                              </button>
                            )}
                          </div>
                          {isAdmin && (
                            <div className="flex gap-1 mt-1">
                              <button onClick={() => onToggleLockVoice && onToggleLockVoice(`${selectedVoiceType}_${phase}`)}
                                className={`text-[9px] px-1.5 py-0.5 rounded ${isLocked ? 'text-amber-400 bg-amber-500/10' : 'text-white/30 bg-white/5'}`}>
                                {isLocked ? 'Déverrouiller' : 'Verrouiller'}
                              </button>
                              {!isLocked && onDeleteVoice && (
                                <button onClick={() => onDeleteVoice(phase, selectedVoiceType)}
                                  className="text-[9px] px-1.5 py-0.5 rounded text-red-400/60 bg-red-500/10 hover:text-red-400">
                                  Supprimer
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : isAdmin ? (
                        <button className="cursor-pointer text-white/40 text-xs flex items-center gap-1"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.mp3,.wav,.ogg,.m4a';
                            input.onchange = (e) => { const file = e.target.files[0]; if (file) onVoiceUpload(phase, file, selectedVoiceType); };
                            input.click();
                          }}>
                          <Upload className="w-3 h-3" /> Ajouter
                        </button>
                      ) : (
                        <span className="text-white/30 text-xs">—</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        )}

        {/* Background Tab */}
        {activeTab === 'background' && (
          <BackgroundTab
            videos={backgrounds}
            images={backgroundImages}
            selectedBg={selectedBg}
            selectedImageBg={selectedImageBg}
            hoveredVideo={hoveredVideo}
            defaultVideoId={defaultVideoId}
            defaultImageId={defaultImageId}
            uploadingVideo={uploadingVideo}
            uploadingImage={uploadingImage}
            selectedAnimation={selectedAnimation}
            onBgSelect={onBgSelect}
            onImageBgSelect={onImageBgSelect}
            onHoverVideo={onHoverVideo}
            onVideoRename={onVideoRename}
            onVideoToggleLock={onVideoToggleLock}
            onVideoDelete={onVideoDelete}
            onSetDefaultVideo={onSetDefaultVideo}
            onVideoUpload={onVideoUpload}
            onImageRename={onImageRename}
            onImageToggleLock={onImageToggleLock}
            onImageDelete={onImageDelete}
            onSetDefaultImage={onSetDefaultImage}
            onImageUpload={onImageUpload}
            onAnimationChange={onAnimationChange}
          />
        )}
      </div>
    </div>
    </div>
  );
};

export default BreathingSettingsPanel;
